import {
  AUTH_RIGHT_PACKAGE_CODES,
  COMPANY_UPGRADE_PLUS,
  getVipTypeById,
  TEM_RIGHT_PACKAGE_CODES,
  UPGRADE_RIGHT_PACKAGE_CODES,
  VIP_CONFIG,
  VIP_RIGHT_IDENTIFICATION,
} from "../constants/vip";
import request from "../utils/request";
import { getUserRightCount } from "./material";
import { getSubscriptionList } from "./order";

export function getVipGoods(category_ids) {
  return request({
    url: "/biz/resources/goods",
    query: {
      category_ids: category_ids || VIP_CONFIG.HBCategoryId,
      page_size: 100,
    },
  });
}

export function getSubscribeActivities(activity_ids) {
  return request({
    url: "/biz/external-subscribe/activities",
    query: {
      activity_ids,
    },
    ignoreMessage: true,
  });
}

/**
 * 获取当前有效会员
 * @returns {Promise<Array>}
 */
export async function getCurrentVip() {
  return request({
    url: `/biz/users/vips`,
    ignoreMessage: true,
  }).then((result) => {
    if (result.err) {
      return [];
    }

    return result.filter((vip) => !vip.is_expired);
  });
}

/**
 * 获取用户所有会员列表
 * @returns
 */
export async function getUserVips() {
  return request({
    url: `/biz/users/vips`,
    ignoreMessage: true,
    ignoreError: true,
  }).then((result) => {
    if (result.err || !Array.isArray(result)) {
      return [];
    }

    return result;
  });
}

/**
 * 获取会员Zone
 * @param {string} system_code 会员体系
 */
export async function getCurrentVipZone(system_code) {
  return request({
    url: `/biz/users/vip-zone`,
    query: {
      system_code,
    },
  });
}

export async function getCurrentVipSubject() {
  return request({
    url: `/biz/users/vip-subjects`,
  });
}

export async function getUpdatePrice(data) {
  return request({
    method: "post",
    url: "/biz/order/calculate",
    data,
  });
}

export function exchangeVip(code) {
  return request({
    url: `/biz/exchage/${code}/user-vips`,
    method: "post",
  });
}

export function submitCompanyVipConsult({
  name,
  mobile,
  company,
  company_scale,
  download_cnt,
  source,
  leads_entrance,
}) {
  return request({
    url: "/crm/leads/create",
    method: "post",
    data: {
      name,
      mobile,
      company,
      company_scale,
      download_cnt,
      source,
      leads_entrance,
    },
  });
}

/**
 * 获取用户会员与权益信息
    vips, 当前会员信息
    tem_rights, 当前生效的24小时素材包信息
    upgrade_rights, 当前购买的升级包权益信息
    auth_rights, 扩展授权包权益信息
    downloadCount, 下载信息
    @returns {{
      err?: number,
      msg?: string,
      vips: [],
      tem_rights: [],
      upgrade_rights: [],
      auth_rights: [],
      downloadCount: [{
        left: number,
        quantity: number,
        usage: number
      }],
    }}
 */
export async function getUserRight() {
  const [vips, subscription] = await Promise.all([
    getCurrentVip(),
    // 获取订阅信息
    getSubscriptionList({ status: "active" }),
  ]);
  const effect_vips = vips.filter((vip) => !vip.is_expired);

  if (effect_vips.length === 0) {
    return {
      vips: [],
      tem_rights: [],
      upgrade_rights: [],
      upgrade_plus_rights: [],
      auth_rights: [],
      effect_rights: [],
      subscription: subscription?.list || [],
      downloadCount: [],
    };
  }

  const [vipDetail, downloadCount] = await Promise.all([
    // 用户会员聚合信息
    // 包含权益、会员vips，vip-zone
    request({
      url: "/biz/users/vip_rights/aggregate",
      query: {
        system_code: effect_vips[0].system_code,
      },
    }),
    // 获取下载次数信息
    getUserRightCount([{ right_code: VIP_RIGHT_IDENTIFICATION }]),
  ]);

  const rights = vipDetail.userRights.list;

  const vip_expire =
    effect_vips.length &&
    Math.min(...effect_vips.map((r) => new Date(r.expires_at).getTime()));

  // 生效权益
  const effect_rights = rights.filter((item) => item.status === 1);

  // 权益包code与最新的权益映射，用于计算买权益的时候，权益起始时间
  const rightPkgCodeMap = {};
  effect_rights.forEach((right) => {
    const preRight = rightPkgCodeMap[right.right_package_code];
    if (preRight) {
      rightPkgCodeMap[right.right_package_code] =
        new Date(preRight.end_at).getTime() > new Date(right.end_at).getTime()
          ? preRight
          : right;
    } else {
      rightPkgCodeMap[right.right_package_code] = right;
    }
  });

  // 升级包权益
  const tem_rights = effect_rights.filter((item) =>
    TEM_RIGHT_PACKAGE_CODES.includes(item.right_package_code)
  );
  const tem_rights_expire =
    tem_rights.length &&
    Math.max(...tem_rights.map((r) => new Date(r.end_at).getTime()));

  // 升级包权益
  const upgrade_rights = effect_rights.filter((item) =>
    UPGRADE_RIGHT_PACKAGE_CODES.includes(item.right_package_code)
  );
  const upgrade_rights_expire =
    upgrade_rights.length &&
    Math.max(...upgrade_rights.map((r) => new Date(r.end_at).getTime()));

  // 升级包Plus权益
  const upgrade_plus_rights = effect_rights.filter(
    (item) => item.right_package_code === COMPANY_UPGRADE_PLUS
  );
  const upgrade_plus_rights_expire =
    upgrade_plus_rights.length &&
    Math.max(...upgrade_plus_rights.map((r) => new Date(r.end_at).getTime()));

  // 升级包权益
  const auth_rights = rights.filter((item) =>
    AUTH_RIGHT_PACKAGE_CODES.includes(item.right_package_code)
  );
  const auth_rights_expire =
    auth_rights.length &&
    Math.max(...auth_rights.map((r) => new Date(r.end_at).getTime()));

  // 每天可下载个数（不包含加油包）
  downloadCount[0].quantity =
    downloadCount[0].quantity - tem_rights.length * 10;

  return {
    vipType: getVipTypeById(effect_vips[0].vip_id),
    vips: effect_vips,
    vipZone: vipDetail.userVipZone,
    tem_rights,
    upgrade_rights,
    auth_rights,
    downloadCount,
    upgrade_plus_rights,
    tem_rights_expire,
    upgrade_rights_expire,
    upgrade_plus_rights_expire,
    auth_rights_expire,
    vip_expire,
    effect_rights,
    subscription: subscription.list,
    rightPkgCodeMap,
  };
}
